// File: src/components/admin/analysis-tasks/ValidationPanel.js
// Displays semantic validation results for analysis task configs (Issue #64)

import React, { useState, useEffect, useRef } from 'react';
import { Box, Alert, AlertTitle, Collapse, Button, Chip, Typography, IconButton } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { validateTaskConfig } from '../../../utils/semanticValidation';

const SEVERITY_MAP = {
    error: 'error',
    warning: 'warning',
    info: 'info',
};

const ValidationPanel = ({ formData, onApplyFix }) => {
    const [validation, setValidation] = useState({ errors: [], warnings: [], info: [] });
    const [expanded, setExpanded] = useState(true);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const result = validateTaskConfig(formData);
            setValidation(result);
        }, 500);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [formData]);

    const allItems = [
        ...validation.errors,
        ...validation.warnings,
        ...validation.info,
    ];

    if (allItems.length === 0) return null;

    const hasErrors = validation.errors.length > 0;
    const hasWarnings = validation.warnings.length > 0;

    return (
        <Box sx={{ mb: 2 }} data-testid="validation-panel">
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', cursor: 'pointer',
                    mb: expanded ? 1 : 0,
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Typography variant="subtitle2" sx={{ mr: 1 }}>
                    Validation
                </Typography>
                {hasErrors && <Chip label={`${validation.errors.length} error${validation.errors.length > 1 ? 's' : ''}`} color="error" size="small" sx={{ mr: 0.5 }} />}
                {hasWarnings && <Chip label={`${validation.warnings.length} warning${validation.warnings.length > 1 ? 's' : ''}`} color="warning" size="small" sx={{ mr: 0.5 }} />}
                {validation.info.length > 0 && <Chip label={`${validation.info.length} info`} color="info" size="small" />}
                <IconButton size="small" sx={{ ml: 'auto' }}>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>
            <Collapse in={expanded}>
                {allItems.map((item, idx) => (
                    <Alert
                        key={`${item.rule}-${idx}`}
                        severity={SEVERITY_MAP[item.severity] || 'info'}
                        sx={{ mb: 0.5, py: 0.5 }}
                        data-testid={`validation-item-${item.rule}`}
                        action={
                            item.suggestedFix && onApplyFix ? (
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => onApplyFix(item)}
                                    data-testid={`validation-fix-${item.rule}`}
                                >
                                    Fix
                                </Button>
                            ) : undefined
                        }
                    >
                        <AlertTitle sx={{ fontSize: '0.8rem', mb: 0 }}>{item.rule.replace(/_/g, ' ')}</AlertTitle>
                        <Typography variant="caption">{item.message}</Typography>
                    </Alert>
                ))}
            </Collapse>
        </Box>
    );
};

export default ValidationPanel;
